import {signDocuments} from "../rest/api";
import util from "../lib/util";
import Document from "../entities/document";
import store from "../store/store";
import {FetchFileCallback, fetchFileWithProgressResult} from '../lib/fetchFileWithProgress';
import * as TaskHelper from '../TaskManagement/TaskHelper';
import CaseHelper from '../Case/CaseHelper';
import LeadHelper from '../CRM/Sales/LeadHelper';
import {saveDocumentQueue} from '../store/actions/documentActions';
import Icons, {IconType} from '../ui/IconButton/Icons';
import Alert from '../lib/alert';
import {getCurrentFirmId, getCurrentUserId, isPortalUser} from "../lib/picos/currentLogin";
import {uuid} from "../@types/schema";
import {EntityNames} from "../entities";
import Task from "../entities/task";
import Contact from "../entities/contact";
import Campaign from "../entities/campaign";
import Lead from "../entities/lead";
import Matter from "../entities/matter";
import Invoice from "../entities/invoice";
import React from "react";
import type {PDFDocumentProxy} from 'pdfjs-dist';
import {loadPdfjs} from '../lib/pdf/pdfjsLoader';
import {refuseOversizedFile} from './uploadLimits';
import {MAX_LOCAL} from './documentUploadRouting';

export type UnsavedEntity = Task | Contact | Campaign | Lead | Matter | Invoice;

export const MIME_TYPES = [
    'aac', 'audio/aac',
    'abw', 'application/x-abiword',
    'arc', 'application/x-freearc',
    'avi', 'video/x-msvideo',
    'azw', 'application/vnd.amazon.ebook',
    'bin', 'application/octet-stream',
    'bmp', 'image/bmp',
    'bz', 'application/x-bzip',
    'bz2', 'application/x-bzip2',
    'csh', 'application/x-csh',
    'css', 'text/css',
    'csv', 'text/csv',
    'doc', 'application/msword',
    'docx', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'eml', 'message/rfc822',
    'eot', 'application/vnd.ms-fontobject',
    'epub', 'application/epub+zip',
    'gx', 'application/gzip',
    'gif', 'image/gif',
    'htm', 'text/html',
    'html', 'text/html',
    'ico', 'image/vnd.microsoft.icon',
    'ics', 'text/calendar',
    'jar', 'application/java-archive',
    'jpeg', 'image/jpeg',
    'jpg', 'image/jpeg',
    'js', 'text/javascript',
    'json', 'application/json',
    'jsonld', 'application/ld+json',
    'log', 'text/plain',
    'mid', 'audio/x-midi',
    'midi', 'audio/x-midi',
    'mjs', 'text/javascript',
    'mp3', 'audio/mpeg',
    'mpeg', 'video/mpeg',
    'mpkg', 'application/vnd.apple.installer+xml',
    'msg', 'application/vnd.ms-outlook',
    'odp', 'application/vnd.oasis.opendocument.presentation',
    'ods', 'application/vnd.oasis.opendocument.spreadsheet',
    'odt', 'application/vnd.oasis.opendocument.text',
    'oga', 'audio/ogg',
    'ogv', 'video/ogg',
    'ogx', 'application/ogg',
    'opus', 'audio/opus',
    'otf', 'font/otf',
    'png', 'image/png',
    'pdf', 'application/pdf',
    'php', 'application/php',
    'ppt', 'application/vnd.ms-powerpoint',
    'pptx', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'rar', 'application/x-rar-compressed',
    'rtf', 'application/rtf',
    'sh', 'application/x-sh',
    'svg', 'image/svg+xml',
    'swf', 'application/x-shockwave-flash',
    'tar', 'application/x-tar',
    'tif', 'image/tiff',
    'tiff', 'image/tiff',
    'ts', 'video/mp2t',
    'ttf', 'font/ttf',
    'txt', 'text/plain',
    'vsd', 'application/vnd.visio',
    'wav', 'audio/wav',
    'weba', 'audio/webm',
    'webm', 'video/webm',
    'webp', 'image/webp',
    'woff', 'font/woff',
    'woff2', 'font/woff2',
    'xhtml', 'application/xhtml+xml',
    'xls', 'application/vnd.ms-excel',
    'xslx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'xml', 'text/xml',
    'xul', 'application/vnd.mozilla.xul+xml',
    'zip', 'application/zip',
    '3gp', 'video/3gpp',
    '3g2', 'video/3gpp2',
    '7z', 'application/x-7z-compressed'
];
export const CURRENT_DOCUMENT_PROTOCOL = 2;

// `friendlyContentType` lives in its own dependency-free file so unit
// tests can import it without bootstrapping the document graph (store,
// React, entities, etc.). Re-exported here for callers that already
// reach for documentHelper.
export {friendlyContentType} from './contentTypeNames';

// ─── Content-based encoding detection ────────────────────────────────────────

/** Known base64 signatures for common binary formats (first few base64 chars of their magic bytes). */
const BASE64_SIGNATURES: Record<string, string[]> = {
    'application/pdf':  ['JVBERi0'],           // %PDF-
    'image/jpeg':       ['/9j/'],              // FF D8 FF
    'image/png':        ['iVBOR'],             // \x89PNG
    'image/gif':        ['R0lGOD'],            // GIF8
    'image/tiff':       ['SUkq', 'TU0A'],      // II* or MM\x00*
    'image/bmp':        ['Qk'],                // BM
    'image/webp':       ['UklGR'],             // RIFF
    'application/zip':  ['UEsDB'],             // PK\x03\x04 (also docx, xlsx, pptx)
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['UEsDB'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':      ['UEsDB'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['UEsDB'],
};

/** MIME prefixes / types that represent binary content. */
const BINARY_MIME_PREFIXES = [
    'image/', 'audio/', 'video/',
    'application/pdf', 'application/msword', 'application/vnd.',
    'application/zip', 'application/x-', 'application/octet-stream',
    'application/rtf', 'application/ogg', 'application/epub',
    'font/',
];

/** Returns true if the content type represents a binary format. */
export function isBinaryMimeType(ct: string): boolean {
    if (!ct) return false;
    return BINARY_MIME_PREFIXES.some(prefix => ct.startsWith(prefix));
}

/**
 * Returns true if the string looks like valid base64.
 * Checks a 256-char sample, validates base64 alphabet, and rejects obvious raw text.
 */
export function isLikelyBase64(s: string): boolean {
    if (!s || s.length < 4) return false;

    // Quick rejection: raw text markers (XML, HTML, JSON, plain text patterns)
    const firstChar = s.charAt(0);
    if (firstChar === '<' || firstChar === '{' || firstChar === '[') return false;

    // Check a 256-char sample against the base64 alphabet (ignoring whitespace)
    const sample = s.substring(0, 256).replace(/[\r\n\s]/g, '');
    if (!/^[A-Za-z0-9+/]+={0,2}$/.test(sample)) return false;

    // Base64 padding only appears at the very end
    const eqIndex = sample.indexOf('=');
    if (eqIndex >= 0 && eqIndex < sample.length - 2) return false;

    return true;
}

/**
 * Safely encode a string to base64, handling UTF-8 and binary edge cases.
 * Uses Buffer.from (available via polyfill) as primary method, with btoa fallback.
 */
function safeBase64Encode(text: string): string {
    try {
        return Buffer.from(text).toString('base64');
    }
    catch {
        try {
            return btoa(unescape(encodeURIComponent(text)));
        }
        catch (e) {
            console.error('safeBase64Encode: all encoding methods failed', e);
            return '';
        }
    }
}

/**
 * Examine buffer content and return a canonical base64 string ready for a data: URL.
 *
 * Handles V1 buffers (already base64), V2 buffers (raw text), data: URLs, and SVG.
 */
export function normalizeDocumentBuffer(buffer: string, contentType: string): string {
    if (!buffer) return '';

    // Strip data: URL prefix if present — extract just the base64 payload
    if (buffer.startsWith('data:')) {
        const commaIndex = buffer.indexOf(',');
        if (commaIndex >= 0) return buffer.substring(commaIndex + 1);
        return buffer;
    }

    // SVG: check before binary MIME (since image/* matches isBinaryMimeType)
    if (contentType === 'image/svg+xml') {
        // base64 SVG: PHN2Zz = <svg, PD94bW = <?xml
        if (buffer.startsWith('PHN2Z') || buffer.startsWith('PD94bW') || isLikelyBase64(buffer)) {
            return buffer; // Already base64
        }
        // Raw SVG XML text → encode
        return safeBase64Encode(buffer);
    }

    // Binary MIME types — check if buffer is already base64 via magic byte signatures
    if (isBinaryMimeType(contentType)) {
        // Check known signatures first
        const sigs = BASE64_SIGNATURES[contentType];
        if (sigs && sigs.some(sig => buffer.startsWith(sig))) {
            return buffer; // Already base64
        }

        // Fallback: heuristic check
        if (isLikelyBase64(buffer)) {
            return buffer; // Already base64
        }

        // Raw content that needs encoding
        return safeBase64Encode(buffer);
    }

    // Text types (text/*, message/rfc822) — check if already base64 first
    if (contentType?.startsWith('text/') || contentType === 'message/rfc822') {
        if (isLikelyBase64(buffer)) return buffer; // Already encoded
        return safeBase64Encode(buffer);
    }

    // Unknown type — try heuristic
    if (isLikelyBase64(buffer)) {
        return buffer;
    }

    // Default: encode as base64
    return safeBase64Encode(buffer);
}

/**
 * Download the given text to the specified fileName.
 * Uses content-based detection to determine encoding.
 *
 * @param {string} fileName
 * @param {string} text
 * @param {string} contentType
 */
export const downloadLocal = (fileName: string, text: string, contentType: string) => {
    const element = document.createElement('a');
    const base64 = normalizeDocumentBuffer(text, contentType);
    element.setAttribute('href', `data:${contentType};base64,${base64}`);
    download(element, fileName);
}

/**
 * Download the given uri to a file.
 *
 * @param {string} fileName
 * @param {string} uri
 */
export const downloadRemote = (fileName: string, uri: string) => {
    const element = document.createElement('a');
    element.setAttribute('href', uri);
    download(element, fileName);
}

/**
 * Create an anchor element, assign it an href, click it, then remove it.
 *
 * @private
 * @param {HTMLElement} element
 * @param {string} fileName
 */
const download = (element: HTMLElement, fileName: string) => {
    element.style.display = 'none';
    element.setAttribute('download', fileName);
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
}

/**
 * Simple helper to get the document's file name. Use the newFileName if available, otherwise use the fileName.
 *
 * @param {Document} doc
 */
export const getFileName = (doc: Document) => doc.newFileName || doc.fileName;

/**
 * Maximum file size to be stored in a document's buffer or plainText.
 *
 * If we are in demo mode, we always store as if local. The one limit lives in
 * documentUploadRouting, which processDocument routes by (OWA-1301).
 */
export const MAX_LOCAL_FILE_SIZE = MAX_LOCAL;

/**
 * Get an Icon for the given contentType (MIME type).
 *
 * @param {string} contentType
 * @returns {IconType}
 */
export function getIcon(contentType: string): IconType {
    if (!contentType) return Icons.FILE;

    switch (contentType) {
        case 'application/pdf':
            return Icons.FILE_PDF;
        case 'application/msword':
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            return Icons.FILE_DOC;
        case 'application/vnd.ms-excel':
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
            return Icons.FILE_XLS;
        case 'application/vnd.ms-powerpoint':
        case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
            return Icons.FILE_PPT;
        case 'message/rfc822':
        case 'application/vnd.ms-outlook':
            return Icons.EMAIL;
        default:
            if (contentType.startsWith('audio/')) return Icons.FILE_AUDIO;
            else if (contentType.startsWith('image/')) return Icons.FILE_IMG;
            else if (contentType.startsWith('video/')) return Icons.FILE_VIDEO;
            else if (contentType === 'text/plain') return Icons.FILE_TEXT;
            else if (contentType === 'text/html') return Icons.FILE_HTML;
            else return Icons.FILE;
    }
}

/**
 * Get all documents related to this entity id and type.
 *
 * @param {uuid} entityId
 * @param {EntityNames} entityName
 * @param {UnsavedEntity} [unsavedEntity]
 */
export function getRelatedDocumentsForEntity(entityId: uuid, entityName: EntityNames, unsavedEntity?: UnsavedEntity) {
    // Get documents directly related to this entity
    const directHits = store.getState().documents
        .getMappingFor('entityId', entityId)
        .filter(doc => !!doc
            && !(doc.private && isPortalUser())
            && doc.status !== 'D'
        );

    // Now go get any documents that can be considered sub-documents (not in client portal though!)
    let taskDocs = [];
    let contactDocs = [];
    let leadDocs = [];
    let matterDocs = [];

    if (!isPortalUser()) {
        const handleTasks = (helper: (id: uuid) => Task[], id: uuid) => {
            const tasks = helper(id);
            tasks.forEach(t => {
                store.getState().documents
                    .filter(d => d.entityId === t.id && d.entityType === 'task' && d.status !== 'D')
                    .forEach(d => taskDocs.push(d));
            })
        }

        const handleLeadTasks = (id: uuid) => handleTasks(TaskHelper.getLeadTasks, id);
        const handleCampaignTasks = (id: uuid) => handleTasks(TaskHelper.getCampaignTasks, id);
        const handleContactTasks = (id: uuid) => handleTasks(TaskHelper.getContactTasks, id);
        const handleMatterTasks = (id: uuid) => handleTasks(TaskHelper.getMatterTasks, id);

        switch (entityName) {
            case 'task':
                // Nothing to do here, this is the atomic level
                break;
            case 'matter':
                // Get all documents for the lead, or tasks belonging to the lead
                const matter: Matter = unsavedEntity?.entityType === 'matter'
                                       ? CaseHelper.getMatterFromId(entityId, unsavedEntity as Matter)
                                       : CaseHelper.getMatterFromId(entityId);

                handleMatterTasks(entityId);

                if (matter) {
                    // Get all documents for the lead
                    leadDocs = store.getState().documents
                        .filter(d => d.entityType === 'lead' && d.entityId === matter.leadId)
                        .filter(util.deletedFilter);

                    if (matter) {
                        // Get all documents for the lead's tasks
                        if (matter.leadId) handleLeadTasks(matter.leadId);

                        // Get all documents for the clients
                        contactDocs = store.getState().documents
                            .filter(d => d.entityType === 'contact' && matter.clients.includes(d.entityId));

                        // Get all documents for the tasks of the related clients
                        matter.clients.forEach(cid => handleContactTasks(cid));
                    }
                }

                break;
            case 'lead':
                // Get all documents for the tasks related to this lead
                handleLeadTasks(entityId);

                // Get all documents for the related clients, and documents for those client's tasks
                const lead = unsavedEntity?.entityType === 'lead'
                             ? LeadHelper.getLeadFromId(entityId, unsavedEntity as Lead)
                             : LeadHelper.getLeadFromId(entityId);

                if (lead) {
                    // Lead could be null if a new lead, thus not yet in documents
                    contactDocs = store.getState().documents
                        .filter(d => d.entityType === 'contact' && lead.clients.includes(d.entityId));

                    lead.clients.forEach(cid => handleContactTasks(cid));
                }

                break;
            case 'campaign':
                // Get all documents related to tasks for this campaign
                handleCampaignTasks(entityId);

                break;
            case 'contact':
                // Get documents for the tasks related to this contact
                handleContactTasks(entityId);

                // Is this contact related to a lead?
                const leads = store.getState().leads.filter(l => l.clients.includes(entityId));
                leads.forEach(lead => {
                    leadDocs = store.getState().documents
                        .filter(d => d.entityType === 'lead' && d.entityId === lead.id);

                    handleLeadTasks(lead.id);
                });

                // Is this contact related to a matter?
                const matters = store.getState().matters.filter(l => l.clients.includes(entityId));
                matters.forEach(matter => {
                    matterDocs = store.getState().documents
                        .filter(d => d.entityType === 'matter' && d.entityId === matter.id);

                    handleMatterTasks(matter.id);
                });

                break;
            default:
                // None of the above?  Oops.
                break;
        }
    }

    return util.sortEntityArrayByDate(
        util.distinctId(directHits.concat(matterDocs)
            .concat(leadDocs)
            .concat(contactDocs)
            .concat(taskDocs)
            .filter(util.existsAndNotDeletedFilter)
        ),
        'addedOn',
        'desc'
    );
}

/**
 * Parse dropTargets from an event, and put them into a list of files for DocumentEditor.processFiles.
 *
 * This gets tricky when directories are involved, as the directory function needs to convert entries to a file,
 * which is an async operation using a callback method.
 *
 * @param {React.DragEvent<HTMLDivElement>} e
 * @returns {Promise<{ dropFiles: File[], docs: Document[] }>}
 * @async
 */
export async function getDropFiles(e: React.DragEvent<HTMLDivElement>): Promise<{ dropFiles: File[], docs: Document[] }> {
    // @ts-ignore
    e.persist();

    const files: File[] = [];
    const docs: Document[] = [];

    // Make sure there is actually a dataTransfer object and some items
    if (e.dataTransfer && e.dataTransfer.items) {
        // Use DataTransferItemList interface to access the file(s)
        const multi = e.dataTransfer.items.length > 1;

        for (let i = 0; i < e.dataTransfer.items.length; i++) {
            const dt = e.dataTransfer.items[i];

            if (!multi && dt.kind === 'string') {
                const contentType = `${dt.type}`;
                // Get the text/uri
                const s = await getAsString(dt);

                // Create a new text item
                let doc = new Document({
                    documentId: util.genUUID(),
                    firmId: getCurrentFirmId(),
                    addedOn: util.newHRTimestamp(),
                    status: 'C',
                    version: 1,
                    protocolVersion: CURRENT_DOCUMENT_PROTOCOL,
                    local: true,
                    fileName: 'Imported Note',
                    fileHandle: null,
                    fileSize: s.length,
                    contentType: contentType,
                    buffer: s
                });

                docs.push(doc);
            }

            else if (!multi && dt.kind === 'file' && dt.type === 'text/plain') {
                const file = dt.getAsFile();

                // OWA-1149: a file past its limit is refused before it is read, and is not handed
                // on as a dropped file either.
                if (refuseOversizedFile(file.name, file.size, file.type || getFileType(file.name))) continue;

                let doc = new Document({
                    documentId: util.genUUID(),
                    firmId: getCurrentFirmId(),
                    addedOn: util.newHRTimestamp(),
                    status: 'C',
                    version: 1,
                    protocolVersion: CURRENT_DOCUMENT_PROTOCOL,
                    local: true,
                    fileName: file.name,
                    fileHandle: file,
                    fileSize: file.size,
                    contentType: file.type
                });

                try {
                    document.body.style.cursor = 'wait';
                    doc = await readFileIntoDocument(doc, false);
                }
                catch (err) {
                    console.error('Error reading file into document', err);
                }
                finally {
                    document.body.style.cursor = 'default';
                }

                docs.push(doc);
            }

            else if (multi && dt.kind === 'string') {
                if (dt.type === 'text/uri-list') {
                    dt.getAsString(uri => {

                        // Break up the uri
                        const pieces = uri.split(':');
                        if (pieces[0]?.startsWith('message')) {
                            Alert.add(new Alert(Alert.ERROR, null, 0,
                                'Microsoft, Goole and Apple have stopped support for dragging an email from your mail client' +
                                ' directly to other applications. The workaround is to drag the email from your email client ' +
                                ' to Finder (Mac) or File Manager (Windows), then drag that into Outlaw Practice. Progress, am I right?',
                                20_000
                            ));
                        }
                        else {
                            console.warn('Can not drop a URL', pieces)
                            Alert.addSimpleInfo('Can not drop a URL', 2500);
                        }
                    });
                }
            }

            // If dropped items aren't files, reject them
            if (dt.kind === 'file') {
                // Get as a file entry, so we can see if it is a directory or a file
                // TODO: Check occasionally to see if this is promoted to non-experimental: getAsEntry(). Last check 2025-01-17.
                const entry = dt.webkitGetAsEntry();

                // If this is a directory, we will parse the whole directory
                const isDirectory = entry.isDirectory;

                // If this is a file, just go ahead and add it.
                if (!isDirectory) {
                    const file = dt.getAsFile();
                    files.push(file);
                }
                else {
                    // This is a directory, so we need to get each file using a callback function
                    const directoryReader = (entry as unknown as FileSystemDirectoryEntry).createReader();

                    const dirFiles = await readEntries(directoryReader);
                    dirFiles.forEach(f => files.push(f));
                }
            }
        }
    }

    return {dropFiles: files.flat(), docs: docs.flat()};
}

/**
 * Get the remote url for this document.
 * Updates the uri and uriExpires for the passed-in document. Does not clone.
 *
 * @param {Document} doc
 */
export async function signDocument(doc: Document) {
    const docs: Document[] = await signDocuments([doc]);
    const signed = docs[0];
    if (!signed) throw new Error('Unable to get a URL for this document');
    else {
        doc.uri = signed.uri;
        doc.uriExpires = signed.uriExpires;
    }
}

/**
 * Is this an image file?
 * @param {Document} doc
 * @returns {boolean}
 */
export function isImageFile(doc: Document): boolean {
    return doc.contentType?.startsWith('image/');
}

/**
 * Figure out the document type based on the mime type.
 *
 * @param {string} name
 */
export function getFileType(name: string): string {
    // Get the suffix, if any
    const pieces = name.split('.');
    const suffix = pieces.length === 1 ? '' : pieces[pieces.length - 1];
    let type = 'application/unknown';

    if (suffix) {
        const index = MIME_TYPES.indexOf(suffix.toLowerCase());
        if (index >= 0) type = MIME_TYPES[index + 1];
    }

    return type;
}

/**
 * Most characters of PDF text kept for a document (OWA-1153). One million
 * characters is about 400 pages of dense text; text past it is not stored.
 */
export const MAX_PDF_TEXT_CHARS = 1_000_000;

// The server's cleanExtractedText rules (server/src/misc/textSanitize.ts).
// PostgreSQL text rejects U+0000, and a garbled or encrypted PDF yields mostly
// control and replacement characters, which is treated as no text. One
// addition: whitespace alone (the page joins of a multi-page scan) is no text.
// eslint-disable-next-line security/detect-non-literal-regexp -- compile-time String.fromCharCode(0), never runtime input.
const NUL = new RegExp(String.fromCharCode(0), 'g');
// eslint-disable-next-line security/detect-non-literal-regexp -- composed only from compile-time String.fromCharCode() constants.
const CONTROL_AND_REPLACEMENT = new RegExp(
    '[' +
    String.fromCharCode(1) + '-' + String.fromCharCode(8) +
    String.fromCharCode(11) + String.fromCharCode(12) +
    String.fromCharCode(14) + '-' + String.fromCharCode(31) +
    String.fromCharCode(127) + '-' + String.fromCharCode(159) +
    String.fromCharCode(0xFFFD) +
    ']',
    'g',
);
const GARBAGE_THRESHOLD = 0.2;

function cleanExtractedText(s: string): string {
    if (!s.trim()) return '';
    const noNul = s.replace(NUL, '');
    const sample = noNul.length > 4000 ? noNul.slice(0, 4000) : noNul;
    const noise = (sample.match(CONTROL_AND_REPLACEMENT) || []).length;
    if (sample && noise / sample.length > GARBAGE_THRESHOLD) return '';
    return noNul.replace(CONTROL_AND_REPLACEMENT, '');
}

// Cut to `max` UTF-16 units without leaving half of a surrogate pair at the end.
function limitText(s: string, max: number): string {
    if (s.length <= max) return s;
    const code = s.charCodeAt(max - 1);
    return s.slice(0, code >= 0xD800 && code <= 0xDBFF ? max - 1 : max);
}

/**
 * Extract the full text of a PDF using pdfjs-dist (already shipped via
 * react-pdf, worker configured globally in client/src/index.tsx). Heavy
 * parsing runs in pdfjs's worker; the main thread only joins strings.
 *
 * Never throws — returns '' on any failure (corrupt bytes, image-only scan,
 * password-protected). The caller still saves the document; empty plainText
 * is the pre-OWA-429 baseline, so a failed extraction is no worse than today.
 *
 * OWA-1153: the text is cleaned as the server's backfill extractor cleans it
 * (server/src/misc/textSanitize.ts cleanExtractedText). What is bounded:
 * - the returned (stored and sent) text is at most MAX_PDF_TEXT_CHARS characters;
 * - each page's text items are streamed from the worker, and reading stops at
 *   the budget, between items and between pages: the page's stream is
 *   cancelled and no later page is read;
 * - any failure returns '', and the caller saves the original file.
 * What is not bounded: pdfjs decodes each text item whole in its worker before
 * this function sees it, so one show-text operator (for example a compressed
 * stream that decodes to millions of glyphs) is built in full and then cut.
 * The document viewer (PdfEditor, the same pdfjs) builds it too when the file
 * is opened.
 * Under the limit the text is the same as before: a page's items joined with
 * ' ', pages joined with '\n\n'.
 *
 * @param {Uint8Array} bytes - raw PDF bytes. pdfjs transfers the buffer to its
 *   worker, so the caller's array is empty afterwards.
 * @param {FetchFileCallback} [cb] - passes back extraction progress 0..1
 * @return {Promise<string>}
 */
export async function extractPdfText(bytes: Uint8Array, cb?: FetchFileCallback): Promise<string> {
    let pdf: PDFDocumentProxy | null = null;
    try {
        // Lazily load pdfjs (keeps the ~409KB engine off the eager boot chunk).
        const pdfjs = await loadPdfjs();
        pdf = await pdfjs.getDocument({data: bytes, disableAutoFetch: true, disableStream: true}).promise;

        let text = '';
        let full = false;
        const add = (s: string) => {
            const room = MAX_PDF_TEXT_CHARS - text.length;
            if (s.length < room) {
                text += s;
                return;
            }
            text += limitText(s, room);
            full = true;
        };

        for (let i = 1; i <= pdf.numPages && !full; i++) {
            if (i > 1) add('\n\n');
            if (full) {
                cb?.(1);
                break;
            }
            const page = await pdf.getPage(i);
            const reader = page.streamTextContent().getReader();
            let first = true;
            while (!full) {
                const {value, done} = await reader.read();
                if (done) break;
                for (const item of value.items) {
                    if (!first) add(' ');
                    first = false;
                    if (!full) add('str' in item ? item.str : '');
                    if (full) break;
                }
            }
            // pdfjs requires an Error reason; AbortException ends the worker's text task without a warning.
            // Not awaited: when the worker has already sent the whole page, pdfjs never
            // answers the cancel, and pdf.destroy() below ends the stream anyway.
            if (full) reader.cancel(new pdfjs.AbortException('PDF text limit reached')).catch(() => {});
            page.cleanup();
            cb?.(full ? 1 : i / pdf.numPages);
        }
        return cleanExtractedText(text);
    }
    catch (err) {
        console.warn('[extractPdfText] failed; saving document with empty plainText', err);
        return '';
    }
    finally {
        if (pdf) await pdf.destroy().catch(() => {});
    }
}

/**
 * Decode the base64 string produced by FileReader.readAsDataURL into bytes.
 *
 * @param {string} b64
 * @return {Uint8Array}
 */
function base64ToBytes(b64: string): Uint8Array {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes;
}

/**
 * Populate `plainText` (also our text-layer backup) for a PDF document, in
 * place. The text is cleaned and capped at MAX_PDF_TEXT_CHARS characters, so
 * text past the cap is not stored (OWA-1153). No-op for non-PDFs. The structured `pii` field is
 * populated server-side in createDocument so the NLP module stays out of the
 * client bundle.
 *
 * @param {Document} document - mutated in place
 * @param {Uint8Array} bytes - raw PDF bytes
 * @param {FetchFileCallback} [cb] - extraction progress passthrough
 */
async function populatePdfText(document: Document, bytes: Uint8Array, cb?: FetchFileCallback): Promise<void> {
    if (document.contentType !== 'application/pdf') return;
    document.plainText = await extractPdfText(bytes, cb);
}

/**
 * Called by onOk, needs a callback function.
 * Reads the file handle in state, saves in document.buffer, then calls createRemoteDocument
 *
 * @param {Document} doc
 * @param {boolean} encoded - should a binary file be encoded to base64. Defaults to true. Text is always read as text.
 * @param {FetchFileCallback} [cb] - passes back the pct% of data read so far
 * @return {Promise<Document>}
 */
export function readFileIntoDocument(doc: Document, encoded: boolean = true, cb?: FetchFileCallback): Promise<Document> {
    return new Promise((resolve, reject) => {
        if (!doc.fileHandle?.name && !doc.uri) {
            reject(new Error('No file handle or URI to read from'));
            return;
        }

        const document = doc.clone();

        // If there is a file, we need to read it into buffer, then call createRemoteDocument
        if (document.fileHandle.name) {
            const reader = new FileReader();

            // OWA-1308: the file is local when the file itself is at most MAX_LOCAL_FILE_SIZE,
            // whatever length its base64 has. Text is always read as text: plainText and a text
            // buffer hold text, and createDocument stores an inline buffer without decoding it.
            const file = document.fileHandle;
            const asText = !encoded || document.contentType?.startsWith('text/');

            // Text is read as bytes so a file that is not UTF-8 is detected, not silently decoded.
            if (asText) reader.readAsArrayBuffer(file);
            else reader.readAsDataURL(file);

            reader.onprogress = (e: ProgressEvent) => {
                cb?.(e.loaded / e.total);
            }

            reader.onloadend = async () => {
                let result: string;
                // A text file that is not UTF-8 keeps a decoded copy for search, but the document
                // cannot hold its bytes, so it is not local and its File goes up.
                let lossy = false;
                if (asText) {
                    const bytes = new Uint8Array((reader.result as ArrayBuffer) ?? new ArrayBuffer(0));
                    try {
                        result = new TextDecoder('utf-8', {fatal: true}).decode(bytes);
                    }
                    catch {
                        result = new TextDecoder('utf-8').decode(bytes);
                        lossy = true;
                    }
                }
                else result = reader.result as string; // A string from readAsDataURL
                const local = file.size <= MAX_LOCAL_FILE_SIZE && !lossy;

                if (document.contentType === 'text/plain') {
                    document.plainText = result || '';
                    document.fileSize = document.plainText.length;
                }
                else {
                    // Fill in the buffer with base64 encoded text
                    if (!asText) { // We have a dataURL. Need to split the result off
                        document.buffer = result.split(',')[1] || '';
                        if (!document.buffer) console.warn(`Empty buffer for ${document.fileName}`);
                        document.fileSize = document.buffer.length;
                    }
                    else {
                        document.buffer = result;
                        document.fileSize = document.buffer.length;
                    }

                    // No longer need these fields, and don't want old values sitting around
                    document.fileHandle = null;

                    // NOTE: Version was set in the editor (to 1) when attaching the file
                    // NOTE: We don't set the hash yet, as we want to calculate that later

                    // OWA-429: index PDFs at upload — extract full plainText
                    // (backup + snippet UX); structured pii is then derived
                    // server-side in createDocument. `encoded` is always true
                    // for binary uploads like PDFs, so the buffer is real
                    // base64 here. Failures are swallowed inside
                    // populatePdfText — never block the save.
                    if (document.contentType === 'application/pdf' && !asText && document.buffer) {
                        await populatePdfText(document, base64ToBytes(document.buffer), cb);
                    }

                    // OWA-1308: a base64 buffer too large to be local is never sent without its
                    // File, which createDocument would store as base64 text. The File goes up
                    // in its place.
                    if (!asText && !local) document.buffer = '';
                }

                if (lossy || (!asText && !local)) {
                    document.fileHandle = file;
                    document.fileSize = file.size;
                }

                document.local = local;

                // Call back with the updated document
                resolve(document);
            };

            reader.onerror = () => {
                reject(new Error('Error reading the document'));
            };

            reader.onabort = () => {
                reject(new Error('Error reading the document -- aborted'));
            };
        }
        else if (document.uri) {
            fetchFileWithProgressResult(document.uri, true, cb)
                .then(async result => {
                    if (result.isOk()) {
                        const data = result.unwrap();

                        if (document.contentType.startsWith('text/')) {
                            document.plainText = data;
                            document.fileSize = data.length;
                        }
                        else if (document.contentType === 'message/rfc822') {
                            document.buffer = data;
                            document.fileSize = data.length;
                        }
                        else {
                            document.buffer = data;
                            document.fileSize = data.length;
                        }

                        // OWA-429: index PDFs re-read from storage (refresh /
                        // audit flows). `data` here is a UTF-8 TextDecoder pass
                        // over the raw bytes — lossy for binary, so it can't be
                        // decoded back to PDF bytes. Re-fetch the URI as a
                        // binary ArrayBuffer instead. Best-effort: an extraction
                        // failure must not break loading the document.
                        if (document.contentType === 'application/pdf') {
                            try {
                                const buf = await fetch(document.uri).then(r => r.arrayBuffer());
                                await populatePdfText(document, new Uint8Array(buf), cb);
                            }
                            catch (err) {
                                console.warn('[readFileIntoDocument] PDF re-extract from URI failed', err);
                            }
                        }

                        resolve(document);
                    }
                    else {
                        const err = result.unwrapErr();
                        if (err.status === 404)
                            reject(new Error(err.message || document.uri
                                   ? `Remote document not found`
                                   : `Could not read file ${document.fileHandle?.name}`
                            ));
                        else
                            reject(new Error(`Failed to load document (HTTP ${err.status})`));
                    }
                })
                .catch(err => {
                    console.error('Error in readFileIntoDocument', err);
                    reject(err);
                });
        }
        else reject(new Error('No file or URI provided'));
    });
}

/**
 * Get text from a dti.
 *
 * @param {DataTransferItem} dt
 * @return {Promise<*>}
 */
export function getAsString(dt: DataTransferItem): Promise<any> {
    return new Promise(resolve => {
        dt.getAsString(s => resolve(s));
    });
}

/**
 * @param {FileSystemFileEntry} fileEntry
 * @returns {Promise<File>}
 */
export function convertToFile(fileEntry: FileSystemFileEntry): Promise<File> {
    return new Promise((resolve, reject) => {
        fileEntry.file(
            file => resolve(file),
            err => reject(err)
        );
    });
}

/**
 * @param {FileSystemDirectoryReader} directoryReader
 * @return {Promise<File[]>}
 */
export function readEntries(directoryReader: FileSystemDirectoryReader): Promise<File[]> {
    return new Promise((resolve, reject) => {
        const files = [];

        directoryReader.readEntries(entries => {
                entries.forEach(async entry => {
                    if (entry.isFile) {
                        files.push(convertToFile(entry as FileSystemFileEntry));
                    }
                    else {
                        const dr = (entry as FileSystemDirectoryEntry).createReader();
                        const dirFiles = readEntries(dr);
                        files.push(dirFiles);
                    }
                });

                Promise
                    .all(files)
                    .then(results => {
                        resolve(results.flat());
                    })
                    .catch(err => reject(err));
            }
        );
    });
}

/**
 * Create a new document file and read in the file contents.
 *
 * @param {File[]} files
 * @param {string[]} tags
 * @returns {Array}
 */
/**
 * Fetch a V1 remote document's content for local rendering.
 *
 * V1 documents stored base64 text in S3 instead of binary. This function fetches
 * the first 512 bytes as text and checks whether it looks like base64. If so, it
 * fetches the full content and returns a display doc with the buffer set and the
 * uri nulled, so renderers use the buffer path. If the content is actual binary
 * (V2), returns null so callers fall back to the signed URI.
 *
 * Only attempts detection for binary MIME types (images, PDFs, etc.) since
 * text types are always stored as text in both V1 and V2.
 *
 * Returns null if the document should use its URI directly.
 */
export async function fetchV1RemoteContent(doc: Document): Promise<Document | null> {
    // Guard: only remote binary docs with a URI
    if (doc.local || !doc.uri || !isBinaryMimeType(doc.contentType)) return null;

    try {
        // Probe first 512 bytes to detect V1 base64 content without downloading the full file
        const probeResponse = await fetch(doc.uri, {
            headers: {'Range': 'bytes=0-511'}
        });

        if (!probeResponse.ok && probeResponse.status !== 206) {
            console.error(`fetchV1RemoteContent: probe failed (${probeResponse.status}) for ${doc.fileName}`);
            return null;
        }

        const probe = await probeResponse.text();

        // If the probe doesn't look like base64, this is a V2 binary doc — URI works directly
        if (!isLikelyBase64(probe)) return null;

        // Confirmed V1: fetch the full base64 content
        const fullResponse = await fetch(doc.uri);
        if (!fullResponse.ok) {
            console.error(`fetchV1RemoteContent: full fetch failed (${fullResponse.status}) for ${doc.fileName}`);
            return null;
        }

        const base64Text = await fullResponse.text();
        const displayDoc = doc.clone();
        displayDoc.buffer = base64Text;
        displayDoc.uri = null;
        return displayDoc;
    }
    catch (err) {
        console.error(`fetchV1RemoteContent: error for ${doc.fileName}`, err);
        return null;
    }
}

export function createDocumentsFromFiles(files: File[], tags: string[] = []): Document[] {
    // ISSUE: The javascript is so fast that we are getting conflicting timestamps.
    // This means that IDB will reject some of them, as they have the same addedOn value.
    // We will need to make them sequential
    const timestamp = util.newHRTimestamp();

    const docs = [];

    for (let i = 0; i < files.length; i++) {
        const file = files[i];

        // OWA-1149: a file past its limit is refused here, before it becomes a document.
        const contentType = file.type || getFileType(file.name);
        if (refuseOversizedFile(file.name, file.size, contentType)) continue;

        let doc = new Document({
            documentId: util.genUUID(),
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: timestamp + i,
            createdBy: getCurrentUserId(),
            status: 'C',
            version: 1,
            protocolVersion: CURRENT_DOCUMENT_PROTOCOL,
            local: true,
            originalName: file.name,
            tags
        });

        doc.plainText = '';
        doc.fileName = file.name;
        doc.fileSize = file.size;
        // doc.lastModified = file.lastModified ? new Date(file.lastModified) : new Date();
        doc.contentType = contentType;
        doc.fileHandle = file;

        // If we had previously renamed this, we need to wipe the rename out.
        doc.nameChange = false;
        doc.newFileName = '';

        docs.push(doc);
    }

    return docs;
}

/**
 * Process files (prep for upload)
 * @param {File[]} files - file handles
 * @param {string} entityType
 * @param {uuid} entityId
 * @returns {Promise<Document[]>}
 */
export async function processFiles(files: File[], entityType: string, entityId: uuid): Promise<Document[]> {
    // ISSUE: The javascript is so fast that we are getting conflicting timestamps.
    // This means that IDB will reject some of them, as they have the same addedOn value.
    // We thus make them sequential instead of real timestamps
    const timestamp = util.newHRTimestamp();

    const docs = [];

    for (let i = 0; i < files.length; i++) {
        const file = files[i];

        const contentType = file.name.endsWith('.msg')
                            ? 'application/vnd.ms-outlook'
                            : file.type || getFileType(file.name);

        // OWA-1149: a file past its limit is refused here, before it is queued.
        if (refuseOversizedFile(file.name, file.size, contentType)) continue;

        const doc = new Document({
            documentId: util.genUUID(),
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: timestamp + i,
            createdBy: getCurrentUserId(),
            status: 'C',
            version: 1,
            protocolVersion: CURRENT_DOCUMENT_PROTOCOL,
            local: true,
            originalName: file.name,
            entityType,
            entityId
        });

        doc.fileName = file.name;
        doc.fileSize = file.size;
        // doc.lastModified = file.lastModified ? new Date(file.lastModified) : new Date();

        doc.contentType = contentType;

        doc.fileHandle = file;

        // If we had previously renamed this, we need to wipe the rename out.
        doc.nameChange = false;
        doc.newFileName = '';
        doc.plainText = '';

        store.dispatch(saveDocumentQueue(doc));

        docs.push(doc);
    }

    return docs;
}